import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLandmark } from "@fortawesome/free-solid-svg-icons";

import AmountWithChange from "$components/amount-with-change";
import LargeNumber from "$components/info-list/content/large-number";
import ScoreBubble from "$components/score-bubble";
import ChartPie from "$components/charts/pie";
import ExternalLegend from "$components/charts/legend/external";
import BarComparison from "$components/charts/bar-comparison";
import TemperatureBar from "$components/charts/temperature-bar";
import TeamHierarchy from "./components/team-hierarchy";

import { calculateScoreMark, checkIsHealthy, getHealthColorClass } from "$utils/health-helper";
import { prefixOtherIndustries } from "$utils/industry-helper";
import { useDocumentTitle } from "$utils/document-title";
import { abbrNum } from "$utils/number-helper";
import { ChartsStore } from "$stores/charts";
import { ClientDetailsStore } from "$stores/client-details";
import { dealProbabilityText } from "$utils/probability-text";
import { aggregateChildren, limitChartData } from "./lib/chart-helper";
import { getTerm } from "$stores/dictionary";
import { setHelpContent } from "$stores/help";
import { HELP_CLIENT_DASHBOARD } from "../help/lib/help-contents";
import { useNavigate } from "react-router-dom";
import { setFilters as setExistingFilters } from "$stores/clientExistingProjects";
import { setFilters as setPotentialFilters } from "$stores/potentialProjects";
import HealthHistory from "./components/health-history";
import { emifyIt } from "$utils/text-helpers";
import { AppContextStore } from "$stores/app-context";
import CollapsingPills from "$components/table/cells/collapsing-pills";
import { ClientSummaryStore, fetchClientSummary, generateClientSummary, resetClientSummary } from "$stores/client-summary";

const renderMarkdown = (text) => {
    const escaped = text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const html = escaped
        .split(/\n\n+/)
        .map((block) => {
            const m2 = block.match(/^## (.+)/);
            if (m2) return `<h2>${m2[1]}</h2>`;
            const m3 = block.match(/^### (.+)/);
            if (m3) return `<h3>${m3[1]}</h3>`;
            return `<p>${block.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")}</p>`;
        })
        .join("");
    return { __html: html };
};

const ClientDashboard = () => {
    useDocumentTitle(`${getTerm("parentClient")} Dashboard`);
    const navigate = useNavigate();
    const { probabilityMatrix } = AppContextStore.useState((a) => {
        return { probabilityMatrix: a.siteConfig?.probabilityMatrix };
    });
    const { hasSubClients, clientDetails, tags } = ClientDetailsStore.useState((s) => {
        return { hasSubClients: s.hasSubClients, clientDetails: s.clientDetails, tags: s.tags };
    });

    const llmEnabled = AppContextStore.useState((s) => s.settings?.externalConnections?.llmEnabled ?? false);
    const { content, loading, timedOut } = ClientSummaryStore.useState((s) => s);
    const clientId = clientDetails?.id;

    const [displayedContent, setDisplayedContent] = useState("");
    const [isTyping, setIsTyping] = useState(false);
    const seenContentRef = useRef(null);

    useEffect(() => {
        if (!content) {
            setDisplayedContent("");
            setIsTyping(false);
            return;
        }
        if (seenContentRef.current === content) {
            setDisplayedContent(content);
            return;
        }
        seenContentRef.current = content;
        setIsTyping(true);
        const words = content.split(" ");
        let i = 0;
        const tick = setInterval(() => {
            i++;
            setDisplayedContent(words.slice(0, i).join(" "));
            if (i >= words.length) {
                clearInterval(tick);
                setIsTyping(false);
            }
        }, 30);
        return () => clearInterval(tick);
    }, [content]);

    const { t12TotalRevenueByClient, t12TotalRevenueByIndustry, t12TotalRevenueByServiceLine, t12TotalRevenueByProjectType, growthByProjectType, growthByIndustry, growthByServiceLine } = ChartsStore.useState((s) => {
        return {
            t12TotalRevenueByClient: limitChartData(s.charts["t12TotalRevenueByClient"], 4),
            t12TotalRevenueByIndustry: limitChartData(s.charts["t12TotalRevenueByIndustry"], 4),
            t12TotalRevenueByServiceLine: limitChartData(s.charts["t12TotalRevenueByServiceLine"], 4),
            t12TotalRevenueByProjectType: limitChartData(s.charts["t12TotalRevenueByProjectType"], 4),
            growthByProjectType: limitChartData(s.charts["growthByProjectType"], 9),
            growthByServiceLine: limitChartData(s.charts["growthByServiceLine"], 9),
            growthByIndustry: limitChartData(s.charts["growthByIndustry"], 9),
        };
    });

    useEffect(() => {
        setHelpContent(HELP_CLIENT_DASHBOARD);
    }, []);

    useEffect(() => {
        if (!llmEnabled || !clientId) return;
        const abortController = new AbortController();
        let mounted = true;
        (async () => {
            await fetchClientSummary(clientId, abortController);
            if (!mounted) return;
            const currentContent = ClientSummaryStore.getRawState().content;
            if (!currentContent) {
                generateClientSummary(clientId);
            }
        })();
        return () => {
            mounted = false;
            abortController.abort();
            resetClientSummary();
        };
    }, [llmEnabled, clientId]);

    const setExistingProjectsFilter = (key, value) => {
        setExistingFilters({ [key]: value });
        navigate(`/client/${clientDetails?.id}/existing-projects#existing-projects`);
    };
    const setPotentialProjectsFilter = (key, value) => {
        // TODO: Service Line
        setPotentialFilters({ [key]: value });
        navigate(`/client/${clientDetails?.id}/growth-potential#potential-projects`);
    };

    return (
        <div className="dashboard-cards">
            {llmEnabled ? (
                <>
                    <div className="card -crm">
                        <TeamHierarchy
                            crms={clientDetails.crms || []}
                            clientManagers={clientDetails.clientManagers || []}
                            cmTitle={`${getTerm("subCustomerRelationsManagers")}${getTerm("clientManagers") ? ` & ${getTerm("clientManagers")}` : ""}`}
                        />
                    </div>

                    <div className="card -office">
                        <div className="office-icon">
                            <FontAwesomeIcon icon={faLandmark} />
                        </div>
                        <div className="info-field">
                            <h5>Primary Office</h5>
                            <h3>{clientDetails?.primaryOfficeName}</h3>
                        </div>
                        <div className="info-field">
                            <h5>{getTerm("serviceLine")}</h5>
                            <h3>{(clientDetails?.practices || []).map((p) => p.text).join(", ")}</h3>
                        </div>
                        <div className="info-field">
                            <h5>{getTerm("industry")}</h5>
                            <h3>{prefixOtherIndustries(clientDetails?.industries || []).map((i) => i.text).join(", ")}</h3>
                        </div>
                        <div className="info-field">
                            <h5>Strategic Importance</h5>
                            <TemperatureBar percent={clientDetails?.sviScore * 10} hasDot={true} isHealthy={false} cssClass={["-importance"]}>
                                <span className="txt -importance -bold">{dealProbabilityText(clientDetails?.sviScore * 10, probabilityMatrix)}</span>
                            </TemperatureBar>
                        </div>
                        <div className="info-field">
                            <h5>Health Score</h5>
                            <TemperatureBar percent={(clientDetails?.healthScore / 700) * 100} hasDot={true} isHealthy={checkIsHealthy(clientDetails?.healthScore)}>
                                <span className={classNames("txt -bold", getHealthColorClass(clientDetails?.healthScore))}>{calculateScoreMark(clientDetails?.healthScore).label}</span>
                            </TemperatureBar>
                        </div>
                    </div>

                    <div className="card -summary -span2">
                        <h5>AI Summary</h5>
                        {loading && (
                            <div className="ai-skeleton">
                                <span className="txt -muted -small">Generating summary…</span>
                                <div className="ai-skeleton__bar" />
                                <div className="ai-skeleton__bar -short" />
                                <div className="ai-skeleton__bar" />
                                <div className="ai-skeleton__bar -short" />
                                <div className="ai-skeleton__bar" />
                            </div>
                        )}
                        {timedOut && (
                            <div>
                                <p className="txt -muted">Summary unavailable.</p>
                                <button className="button -small" onClick={resetClientSummary}>Dismiss</button>
                            </div>
                        )}
                        {displayedContent && (
                            <div className="ai-content">
                                <div dangerouslySetInnerHTML={renderMarkdown(displayedContent)} />
                                {isTyping && <span className="ai-cursor" />}
                            </div>
                        )}
                    </div>
                </>
            ) : (
                <>
                    <div className="card -crm">
                        <TeamHierarchy
                            crms={clientDetails.crms || []}
                            clientManagers={clientDetails.clientManagers || []}
                            cmTitle={`${getTerm("subCustomerRelationsManagers")}${getTerm("clientManagers") ? ` & ${getTerm("clientManagers")}` : ""}`}
                        />
                    </div>

                    <div className="card -office">
                        <div className="icon">
                            <FontAwesomeIcon icon={faLandmark} />
                        </div>
                        <h5>Primary Office</h5>
                        <h3>{clientDetails?.primaryOfficeName}</h3>
                    </div>

                    <div className="card -office">
                        <div className="txt-left">
                            <h5>{getTerm("serviceLine")}</h5>
                            <h3>{(clientDetails?.practices || []).map((p) => p.text).join(", ")}</h3>
                            <h5>{getTerm("industry")}</h5>
                            <h3>{prefixOtherIndustries(clientDetails?.industries || []).map((i) => i.text).join(", ")}</h3>
                            {clientDetails?.mergers && clientDetails.mergers.length > 0 && (
                                <>
                                    <h5>Merger</h5>
                                    <h3>{(clientDetails?.mergers).map((i) => i?.text).join(", ")}</h3>
                                </>
                            )}
                            {tags && tags.length > 0 && (
                                <>
                                    <h5>Tags</h5>
                                    <CollapsingPills data={tags} analyticsLabel="Tags" modifierClassNames={["-left"]} />
                                </>
                            )}
                        </div>
                    </div>

                    <div className="card -office" style={{ textAlign: "left" }}>
                        <div className="score-rows">
                            <TemperatureBar percent={clientDetails?.sviScore * 10} hasDot={true} isHealthy={false} cssClass={["-importance"]}>
                                <span>Strategic Importance: </span>
                                <span className="txt -importance -bold">{dealProbabilityText(clientDetails?.sviScore * 10, probabilityMatrix)}</span>
                            </TemperatureBar>
                            <TemperatureBar percent={(clientDetails?.healthScore / 700) * 100} hasDot={true} isHealthy={checkIsHealthy(clientDetails?.healthScore)}>
                                <span>Health Score: </span>
                                <span className={classNames("txt -bold", getHealthColorClass(clientDetails?.healthScore))}>{calculateScoreMark(clientDetails?.healthScore).label}</span>
                            </TemperatureBar>
                        </div>
                    </div>
                </>
            )}

            <div className="card -header">
                <h2>
                    <span data-tooltip={getTerm("tooltip12MonthRevenue")} className="tooltip--up">
                        {emifyIt(getTerm("t12Revenue"))}
                    </span>
                </h2>
                <AmountWithChange
                    isHealthy={true}
                    value={`$${abbrNum(clientDetails?.twelveMonthRevenue)}`}
                    changeValue={`$${abbrNum(clientDetails?.twelveMonthRevenueChange)}`}
                    changePercent={abbrNum(clientDetails?.twelveMonthRevenueChangePercent, { showSuffix: false })}
                />
            </div>

            <div className="card">
                <h5 className="txt -center">
                    <span data-tooltip={getTerm("tooltip12MonthRevenue")}>{getTerm("t12Revenue")}</span> By {getTerm("subClient")}
                </h5>

                <div className="chart-legend">
                    <BarComparison
                        data={t12TotalRevenueByClient}
                        onClick={(data) => {
                            setExistingProjectsFilter("clientName", data.text);
                        }}
                        preSup="$"
                    />
                </div>
            </div>

            <div className="card">
                <h5 className="txt -center -white">
                    <span data-tooltip={getTerm("tooltip12MonthRevenue")}>{getTerm("t12Revenue")}</span> By {getTerm("serviceLine")}
                </h5>

                <div className="chart-legend">
                    <BarComparison
                        data={t12TotalRevenueByServiceLine}
                        onClick={(data) => {
                            setExistingProjectsFilter("serviceLine", [{ text: data?.text, value: data.value }]);
                        }}
                        preSup="$"
                    />
                </div>
            </div>
            <div className="card">
                <h5 className="txt -center -white">
                    <span data-tooltip={getTerm("tooltip12MonthRevenue")}>{getTerm("t12Revenue")}</span> By {getTerm("industry")}
                </h5>

                <div className="chart-legend">
                    <BarComparison
                        data={t12TotalRevenueByIndustry}
                        onClick={(data) => {
                            setExistingProjectsFilter("industry", data.text);
                        }}
                        preSup="$"
                    />
                </div>
            </div>
            <div className="card">
                <h5 className="txt -center -white">
                    <span data-tooltip={getTerm("tooltip12MonthRevenue")}>{getTerm("t12Revenue")}</span> By {getTerm("projectTypes")}
                </h5>

                <div className="chart-legend">
                    <BarComparison
                        data={t12TotalRevenueByProjectType}
                        onClick={(data) => {
                            setExistingProjectsFilter("projectType", data.text);
                        }}
                        preSup="$"
                    />
                </div>
            </div>

            <div className="card -header">
                <h2>
                    {emifyIt(getTerm("growthPotential"))} {/* TODO: EM-ify */}
                </h2>
                <AmountWithChange
                    value={`$${abbrNum(clientDetails?.growthPotential)}`}
                    changeValue={`$${abbrNum(clientDetails?.growthPotentialChange)}`}
                    changePercent={abbrNum(clientDetails?.growthPotentialChangePercent, { showSuffix: false })}
                />
            </div>
            {hasSubClients && (
                <>
                    {growthByIndustry && (
                        <div className="card -span2">
                            <h4 className="txt -center -white">
                                {getTerm("growthPotential")} By {getTerm("industry")} & {getTerm("subClient")}
                            </h4>
                            <div className="chart-legend">
                                <ChartPie
                                    charts={[
                                        {
                                            data: growthByIndustry,
                                            outerRadius: 105,
                                            innerRadius: 50,
                                            onClick: (data) => {
                                                setPotentialProjectsFilter("industry", data?.label);
                                            },
                                        },
                                        {
                                            data: aggregateChildren(growthByIndustry),
                                            outerRadius: 170,
                                            innerRadius: 115,
                                            onClick: (data) => {
                                                setPotentialProjectsFilter("clientName", data?.label);
                                            },
                                        },
                                    ]}
                                />
                                <ExternalLegend
                                    data={growthByIndustry}
                                    onClick={(data) => {
                                        setPotentialProjectsFilter("industry", data?.label);
                                    }}
                                />
                            </div>
                        </div>
                    )}
                    {growthByServiceLine && (
                        <div className="card -span2">
                            <h4 className="txt -center -white">
                                {getTerm("growthPotential")} By {getTerm("serviceLine")} & {getTerm("projectType")}
                            </h4>
                            <div className="chart-legend">
                                <ChartPie
                                    charts={[
                                        {
                                            data: growthByServiceLine,
                                            outerRadius: 105,
                                            innerRadius: 50,
                                            onClick: (data) => {
                                                setPotentialProjectsFilter("serviceLine", [{ text: data?.label, value: data?.labelId }]);
                                            },
                                        },
                                        {
                                            data: aggregateChildren(growthByServiceLine),
                                            outerRadius: 170,
                                            innerRadius: 115,
                                            onClick: (data) => {
                                                setPotentialProjectsFilter("name", data?.label);
                                            },
                                        },
                                    ]}
                                />
                                <ExternalLegend
                                    data={growthByServiceLine}
                                    onClick={(data) => {
                                        setPotentialProjectsFilter("serviceLine", [{ text: data?.label, value: data?.labelId }]);
                                    }}
                                />
                            </div>
                        </div>
                    )}
                </>
            )}
            {!hasSubClients && (
                <>
                    <div className="card -span2">
                        <h4 className="txt -center -white">
                            {getTerm("growthPotential")} By {getTerm("serviceLine")}
                        </h4>
                        <div className="chart-legend">
                            <ChartPie
                                charts={[
                                    {
                                        data: growthByServiceLine,
                                        outerRadius: 105,
                                        innerRadius: 50,
                                        onClick: (data) => {
                                            setPotentialProjectsFilter("serviceLine", [{ text: data?.label, value: data?.labelId }]);
                                        },
                                    },
                                ]}
                            />
                            <ExternalLegend
                                data={growthByServiceLine}
                                onClick={(data) => {
                                    setPotentialProjectsFilter("serviceLine", [{ text: data?.label, value: data?.labelId }]);
                                }}
                            />
                        </div>
                    </div>

                    <div className="card -span2">
                        <h4 className="txt -center -white">
                            {getTerm("growthPotential")} By {getTerm("projectType")}
                        </h4>
                        <div className="chart-legend">
                            <ChartPie
                                charts={[
                                    {
                                        data: growthByProjectType,
                                        outerRadius: 105,
                                        innerRadius: 50,
                                        onClick: (data) => {
                                            setPotentialProjectsFilter("name", data?.label);
                                        },
                                    },
                                ]}
                            />
                            <ExternalLegend
                                data={growthByProjectType}
                                onClick={(data) => {
                                    setPotentialProjectsFilter("name", data?.label);
                                }}
                            />
                        </div>
                    </div>
                </>
            )}

            <div className="card -header">
                <h2>
                    <em>Health Score</em> Over Time
                </h2>
                <ScoreBubble
                    label="Health Score"
                    graph={true}
                    value={clientDetails?.healthScore}
                    healthy={checkIsHealthy(clientDetails?.healthScore)}
                    percent={(clientDetails?.healthScore / 700) * 100}
                    modifierClassNames={["-small -center"]}
                />
            </div>
            <div className="card -span4">
                <HealthHistory />
            </div>
        </div>
    );
};

export default ClientDashboard;
